import React from 'react';
import { getOperateType, useFramework, useIsBasicOperate, useScale } from '@/framework';
import { isLeftButton } from '@/utils/event-kit';
import { BaseLayerViewType, type ILayerProps } from '@/version-types';
import { EdgelessChartLayer, type ChartTooltipInfo } from '../edgeless-layer';
import './chart-hover-operate.less';

const CHART_TOOLTIP_FADE_DURATION = 120;
const CHART_TOOLTIP_OFFSET = 6;
const CHART_TOOLTIP_ZOOM_SETTLE_DURATION = 120;

interface ChartHoverOperateProps {
    layer: ILayerProps;
    mode?: 'hover' | 'active';
}

export const ChartHoverOperate: React.FC<ChartHoverOperateProps> = ({ layer, mode = 'hover' }) => {
    const framework = useFramework();
    const scale = useScale();
    const isBasicOperate = useIsBasicOperate();
    const isBasicOperateRef = React.useRef(isBasicOperate);
    const chartObjectRef = React.useRef<EdgelessChartLayer | null>(null);
    const unsubscribeTooltipRef = React.useRef<(() => void) | null>(null);
    const mouseDownPointRef = React.useRef<{ x: number; y: number } | null>(null);
    const showFrameRef = React.useRef<number | null>(null);
    const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const zoomSettleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const isCanvasZoomingRef = React.useRef(false);
    const [tooltipInfo, setTooltipInfo] = React.useState<ChartTooltipInfo | null>(null);
    const [renderedTooltipInfo, setRenderedTooltipInfo] = React.useState<ChartTooltipInfo | null>(null);
    const [isTooltipVisible, setIsTooltipVisible] = React.useState(false);

    isBasicOperateRef.current = isBasicOperate;

    const clearTooltipFadeTimer = React.useCallback(() => {
        if (showFrameRef.current !== null) {
            window.cancelAnimationFrame(showFrameRef.current);
            showFrameRef.current = null;
        }
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const getChartObject = React.useCallback(() => {
        const object = framework.service.edgelessCanvas.getByLayerId(layer.id);
        return object instanceof EdgelessChartLayer ? object : null;
    }, [framework, layer.id]);

    const subscribeChartObject = React.useCallback(() => {
        const chartObject = getChartObject();
        if (chartObjectRef.current === chartObject) return chartObject;

        unsubscribeTooltipRef.current?.();
        unsubscribeTooltipRef.current = null;
        chartObjectRef.current = chartObject;
        setTooltipInfo(null);

        if (chartObject) {
            unsubscribeTooltipRef.current = chartObject.onChartTooltipChange(setTooltipInfo);
        }

        return chartObject;
    }, [getChartObject]);

    const hideTooltip = React.useCallback(() => {
        getChartObject()?.hideChartTooltip();
    }, [getChartObject]);

    const hideTooltipImmediately = React.useCallback(() => {
        clearTooltipFadeTimer();
        hideTooltip();
        setTooltipInfo(null);
        setRenderedTooltipInfo(null);
        setIsTooltipVisible(false);
    }, [clearTooltipFadeTimer, hideTooltip]);

    const dispatchToChart = React.useCallback(
        (type: string, sourceEvent: MouseEvent) => {
            if (isBasicOperateRef.current || isCanvasZoomingRef.current) return;

            const chartObject = subscribeChartObject();
            if (!chartObject) return;

            chartObject.dispatchChartHoverEvent(type, framework.service.transform.getScenePoint(sourceEvent));
        },
        [framework, subscribeChartObject],
    );

    React.useEffect(() => {
        const { edgelessCanvas, events } = framework.service;
        const listenerId = edgelessCanvas.onZoom(() => {
            isCanvasZoomingRef.current = true;
            hideTooltipImmediately();

            if (zoomSettleTimerRef.current) {
                clearTimeout(zoomSettleTimerRef.current);
            }
            zoomSettleTimerRef.current = setTimeout(() => {
                zoomSettleTimerRef.current = null;
                isCanvasZoomingRef.current = false;
            }, CHART_TOOLTIP_ZOOM_SETTLE_DURATION);
        });

        return () => {
            events.off(listenerId);
            if (zoomSettleTimerRef.current) {
                clearTimeout(zoomSettleTimerRef.current);
                zoomSettleTimerRef.current = null;
            }
            isCanvasZoomingRef.current = false;
        };
    }, [framework.service, hideTooltipImmediately]);

    const dispatchSelectToChart = React.useCallback(
        (sourceEvent: MouseEvent) => {
            if (isBasicOperateRef.current) return false;

            const chartObject = subscribeChartObject();
            if (!chartObject) return false;

            return chartObject.dispatchChartSelectEvent(framework.service.transform.getScenePoint(sourceEvent));
        },
        [framework, subscribeChartObject],
    );

    React.useEffect(() => {
        subscribeChartObject();
        return () => {
            unsubscribeTooltipRef.current?.();
            unsubscribeTooltipRef.current = null;
            chartObjectRef.current = null;
        };
    }, [subscribeChartObject]);

    React.useEffect(() => {
        const handleMouseMove = (event: React.MouseEvent) => {
            if (mode === 'active' && getOperateType(event.target) !== BaseLayerViewType.MOVE) {
                hideTooltip();
                return;
            }

            dispatchToChart('mousemove', event.nativeEvent);
        };

        const listenerId = framework.service.editor.onMouseMove(handleMouseMove, -1);
        return () => {
            framework.service.events.off(listenerId);
        };
    }, [dispatchToChart, framework, hideTooltip, mode]);

    React.useEffect(() => {
        if (mode !== 'active') {
            mouseDownPointRef.current = null;
            return;
        }

        const checkIsChartBodyEvent = (event: React.MouseEvent) => {
            return getOperateType(event.target) === BaseLayerViewType.MOVE;
        };

        const handleMouseDown = (event: React.MouseEvent) => {
            if (!isLeftButton(event.nativeEvent) || !checkIsChartBodyEvent(event)) {
                mouseDownPointRef.current = null;
                return;
            }

            mouseDownPointRef.current = {
                x: event.pageX,
                y: event.pageY,
            };
        };

        const handleMouseUp = (event: React.MouseEvent) => {
            const mouseDownPoint = mouseDownPointRef.current;
            mouseDownPointRef.current = null;

            if (!mouseDownPoint || !isLeftButton(event.nativeEvent) || !checkIsChartBodyEvent(event)) return;
            if (mouseDownPoint.x !== event.pageX || mouseDownPoint.y !== event.pageY) return;

            dispatchSelectToChart(event.nativeEvent);
        };

        const mouseDownListenerId = framework.service.editor.onMouseDown(handleMouseDown, -1);
        const mouseUpListenerId = framework.service.editor.onMouseUp(handleMouseUp, -1);
        return () => {
            framework.service.events.off(mouseDownListenerId);
            framework.service.events.off(mouseUpListenerId);
        };
    }, [dispatchSelectToChart, framework, mode]);

    React.useEffect(() => {
        return () => {
            hideTooltip();
        };
    }, [hideTooltip]);

    React.useEffect(() => {
        const handleMouseLeave = () => hideTooltip();
        document.addEventListener('mouseleave', handleMouseLeave);
        return () => {
            document.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [hideTooltip]);

    React.useEffect(() => {
        if (tooltipInfo) {
            clearTooltipFadeTimer();
            setRenderedTooltipInfo(tooltipInfo);
            showFrameRef.current = window.requestAnimationFrame(() => {
                showFrameRef.current = null;
                setIsTooltipVisible(true);
            });
            return;
        }

        clearTooltipFadeTimer();
        setIsTooltipVisible(false);
        hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null;
            setRenderedTooltipInfo(null);
        }, CHART_TOOLTIP_FADE_DURATION);
    }, [clearTooltipFadeTimer, tooltipInfo]);

    React.useEffect(() => {
        return () => {
            clearTooltipFadeTimer();
        };
    }, [clearTooltipFadeTimer]);

    if (!renderedTooltipInfo) return null;

    const { style } = renderedTooltipInfo;
    const markerSize = style.markerSize * scale;
    const tooltipOpacity = layer.opacity ?? 1;
    const tooltipStyle = {
        '--chart-tooltip-opacity': tooltipOpacity,
        left: renderedTooltipInfo.x * scale,
        top: renderedTooltipInfo.y * scale,
        color: style.color,
        fontFamily: style.fontFamily,
        fontSize: style.fontSize * scale,
        padding: `${style.paddingY * scale}px ${style.paddingX * scale}px`,
        transform: `translate(${CHART_TOOLTIP_OFFSET}px, ${CHART_TOOLTIP_OFFSET}px)`,
    } as React.CSSProperties;

    return (
        <div className={`chart-hover-operate__tip${isTooltipVisible ? ' is-visible' : ''}`} style={tooltipStyle}>
            {renderedTooltipInfo.title ? (
                <div className="chart-hover-operate__tip-title">{renderedTooltipInfo.title}</div>
            ) : null}
            {renderedTooltipInfo.items.map((item, index) => (
                <div className="chart-hover-operate__tip-row" key={`${item.name}-${index}`}>
                    <span
                        className="chart-hover-operate__tip-marker"
                        style={{
                            width: markerSize,
                            height: markerSize,
                            borderRadius: markerSize / 2,
                            backgroundColor: item.color,
                        }}
                    />
                    <span className="chart-hover-operate__tip-name">{item.name}</span>
                    <span className="chart-hover-operate__tip-value">{item.value}</span>
                </div>
            ))}
        </div>
    );
};

ChartHoverOperate.displayName = 'ChartHoverOperate';
